import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Metric,
  Card,
  TextInput,
  Select,
  Tabs,
  FilterBar,
  Table,
  CellText,
  Badge,
  Avatar,
  Menu,
  Pagination,
  Modal,
  Alert,
  EmptyState,
  SearchLg,
  Plus,
  DotsVertical,
  Edit01,
  Trash01,
} from '@opendate-app/setlist';
const qs = require('qs');

import CrmPageLayout from '../../shared/components/CrmPageLayout';
import GrantFormModal from './GrantFormModal';
import GrantsPipelineBoard from './GrantsPipelineBoard';
import DeadlineFilterOptions from './DeadlineFilterOptions';
import { grantStageConfig, nextDeadlineText, toAmount } from '../grantStages';
import { SORT_OPTIONS, DEFAULT_SORT, buildGrantFilters, buildGrantQuery } from '../grantFiltersConfig';
import { formatMoney } from '../../shared/utils/crmMoney';
import { initialsFor } from '../../Deals/components/dealStages';

const PER_PAGE = 25;

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };
const metricGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--setlist-spacing-xl)' };
const toolbar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--setlist-spacing-lg)',
  padding: 'var(--setlist-spacing-xl) var(--setlist-spacing-3xl)',
};
const toolbarActions = { display: 'flex', alignItems: 'center', gap: 'var(--setlist-spacing-lg)' };
const section = { padding: 'var(--setlist-spacing-lg) var(--setlist-spacing-3xl)' };
const ownerCell = { display: 'flex', alignItems: 'center', gap: 'var(--setlist-spacing-md)' };

const money = (value) => formatMoney(Number(value)) || '$0';

const optionalMoney = (value) => {
  const amount = toAmount(value);
  return amount === null ? '—' : money(amount);
};

const pluralGrants = (count) => `${count.toLocaleString()} ${count === 1 ? 'grant' : 'grants'}`;

const replaceUrlParams = (params) => {
  const query = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
};

function GrantsPage({ team, csrfToken, owners = [], currentOwnerId = null, stages = [] }) {
  const [grants, setGrants] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState(null);
  const [funders, setFunders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const [viewMode, setViewMode] = useState(() => (
    new URLSearchParams(window.location.search).get('view') === 'pipeline' ? 'pipeline' : 'list'
  ));
  const [boardCount, setBoardCount] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);

  const [grantToRemove, setGrantToRemove] = useState(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const { q, due } = useMemo(() => buildGrantQuery(searchTerm, filterValues), [searchTerm, filterValues]);
  const queryKey = JSON.stringify(q);

  const filters = useMemo(() => buildGrantFilters({
    owners,
    funders,
    renderDeadline: (props) => <DeadlineFilterOptions {...props} />,
  }), [owners, funders]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const openCreateForm = () => {
    setEditingGrant(null);
    setShowForm(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') !== 'new') return;
    openCreateForm();
    params.delete('action');
    replaceUrlParams(params);
  }, []);

  useEffect(() => {
    if (!team || !team.id) return undefined;
    if (viewMode !== 'list') return undefined;

    let ignore = false;

    const fetchGrants = async () => {
      try {
        setLoading(true);
        setError(null);
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

        const params = { page: currentPage, per_page: PER_PAGE, sort: sortOrder, q };
        if (due) params.due = due;

        const response = await axios.get(`/teams/${team.id}/crm/grants.json`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
        });
        if (ignore) return;

        const data = response.data || {};
        const nextPagination = data.pagination || {};
        if (nextPagination.total_pages >= 1 && currentPage > nextPagination.total_pages) {
          setCurrentPage(nextPagination.total_pages);
          return;
        }
        setGrants(data.grants || []);
        setPagination(nextPagination);
        setSummary(data.summary || null);
        setFunders(data.funders || []);
      } catch (e) {
        if (ignore) return;
        setError('Failed to load grants');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchGrants();
    return () => { ignore = true; };
  }, [team && team.id, csrfToken, currentPage, sortOrder, queryKey, due, viewMode, refreshKey]);

  const changeViewMode = (mode) => {
    setBoardCount(null);
    setViewMode(mode);
    const params = new URLSearchParams(window.location.search);
    if (mode === 'pipeline') {
      params.set('view', 'pipeline');
    } else {
      params.delete('view');
    }
    replaceUrlParams(params);
  };

  const changeFilters = (next) => {
    setCurrentPage(1);
    setFilterValues(next);
  };

  const handleBoardLoaded = (loaded) => {
    setSummary(loaded.summary || null);
    setFunders(loaded.funders || []);
    setBoardCount(loaded.count);
  };

  const refresh = () => setRefreshKey((prev) => prev + 1);

  const openEditForm = (grant) => {
    setEditingGrant(grant);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingGrant(null);
  };

  const closeRemoveModal = () => {
    setGrantToRemove(null);
    setRemoveSubmitting(false);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!grantToRemove || removeSubmitting) return;

    try {
      setRemoveSubmitting(true);
      setRemoveError(null);
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      await axios.delete(`/teams/${team.id}/crm/grants/${grantToRemove.id}.json`);

      closeRemoveModal();
      refresh();
    } catch (err) {
      const resp = err.response;
      setRemoveError(resp && Array.isArray(resp.data) ? resp.data.join(', ') : 'Failed to delete grant');
      setRemoveSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Grant',
      primary: true,
      render: (grant) => (
        <CellText
          primary={<Button as="a" variant="link-color" href={`/teams/${team.id}/crm/grants/${grant.id}`}>{grant.name}</Button>}
          supporting={grant.funder || undefined}
        />
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (grant) => grant.organization_name || grant.primary_fan_name || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (grant) => {
        const { color, label } = grantStageConfig(grant.stage);
        return <Badge color={color} dot>{label}</Badge>;
      },
    },
    {
      key: 'requested',
      header: 'Requested',
      align: 'right',
      render: (grant) => optionalMoney(grant.requested_amount),
    },
    {
      key: 'awarded',
      header: 'Awarded',
      align: 'right',
      render: (grant) => optionalMoney(grant.awarded_amount),
    },
    {
      key: 'next_deadline',
      header: 'Next deadline',
      render: (grant) => {
        const { dateText, line } = nextDeadlineText(grant.next_deadline);
        return <CellText primary={dateText} supporting={line} />;
      },
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (grant) => (grant.owner_name ? (
        <div style={ownerCell}>
          <Avatar size="sm" src={grant.owner_avatar_url} initials={initialsFor(grant.owner_name)} alt={grant.owner_name} />
          <CellText primary={grant.owner_name} />
        </div>
      ) : '—'),
    },
    {
      key: 'actions',
      header: '',
      width: '48px',
      render: (grant) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="Grant actions" />}
          items={[
            { label: 'Edit grant', icon: Edit01, onClick: () => openEditForm(grant) },
            { label: 'Delete grant', icon: Trash01, destructive: true, onClick: () => setGrantToRemove(grant) },
          ]}
        />
      ),
    },
  ];

  const displayCount = (viewMode === 'pipeline' ? boardCount : pagination.total_entries) || 0;
  const totalPages = pagination.total_pages || 1;

  const tiles = summary ? [
    { label: 'Total requested', value: money(summary.total_requested), hint: `${summary.active_count} active grants` },
    {
      label: 'Total awarded',
      value: money(summary.total_awarded),
      hint: `${summary.won_count} of ${summary.decided_count} decided grants funded`,
    },
    { label: 'Awaiting decision', value: money(summary.awaiting_amount), hint: `${summary.submitted_count} submitted` },
    { label: 'Due in 30 days', value: summary.due_30_count, hint: `${summary.due_7_count} within a week` },
  ] : [];

  return (
    <CrmPageLayout team={team} activeSidebarKey="grants">
      <div style={stack}>
        <PageHeader
          breadcrumbs={(
            <Breadcrumbs
              items={[
                { label: 'CRM', href: `/teams/${team.id}/crm_dashboard` },
                { label: 'Grants' },
              ]}
            />
          )}
          title="Grants"
          supportingText="Track applications, deadlines, awards and reporting for the organizations you fundraise for."
          actions={<Button variant="primary" icon={Plus} onClick={openCreateForm}>New grant</Button>}
        />

        {summary && (
          <div style={metricGrid}>
            {tiles.map((tile) => (
              <Metric key={tile.label} label={tile.label} value={tile.value} change={{ label: tile.hint }} />
            ))}
          </div>
        )}

        <Card padding="none">
          <div style={toolbar}>
            <div style={{ flex: '1 1 280px', maxWidth: 360 }}>
              <TextInput
                leadingIcon={SearchLg}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search grants, funders, organizations"
                aria-label="Search grants"
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              />
            </div>

            <div style={toolbarActions}>
              {viewMode === 'list' && (
                <Select
                  aria-label="Sort grants"
                  width={220}
                  options={SORT_OPTIONS}
                  value={sortOrder}
                  onChange={(value) => { setCurrentPage(1); setSortOrder(value || DEFAULT_SORT); }}
                />
              )}

              <Tabs
                variant="button-border"
                size="sm"
                aria-label="View mode"
                collapseBelow={0}
                value={viewMode}
                onChange={changeViewMode}
                items={[
                  { value: 'list', label: 'List' },
                  { value: 'pipeline', label: 'Pipeline' },
                ]}
              />
            </div>
          </div>

          <div style={section}>
            <FilterBar
              variant="plain"
              filters={filters}
              values={filterValues}
              onChange={changeFilters}
              resultCount={pluralGrants(displayCount)}
            />
          </div>

          {viewMode === 'pipeline' ? (
            <div style={section}>
              <GrantsPipelineBoard
                team={team}
                csrfToken={csrfToken}
                q={q}
                due={due}
                refreshKey={refreshKey}
                onLoaded={handleBoardLoaded}
                onMoved={refresh}
              />
            </div>
          ) : (
            <>
              {error ? (
                <div style={section}>
                  <Alert color="error" title="Failed to load grants" description={error} />
                </div>
              ) : (
                <Table
                  columns={columns}
                  data={grants}
                  getRowKey={(grant) => grant.id}
                  loading={loading}
                  loadingLabel="Loading grants…"
                  empty={(
                    <EmptyState
                      size="sm"
                      icon={SearchLg}
                      title="No grants match these filters."
                      description="Try clearing a filter or searching for a different funder."
                    />
                  )}
                />
              )}

              {!loading && !error && (totalPages > 1 || currentPage > 1) && (
                <div style={section}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      if (page >= 1 && page <= totalPages) setCurrentPage(page);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <GrantFormModal
        isOpen={showForm}
        onClose={closeForm}
        grant={editingGrant}
        team={team}
        csrfToken={csrfToken}
        owners={owners}
        currentOwnerId={currentOwnerId}
        stages={stages}
        funders={funders}
        onSaved={refresh}
      />

      <Modal
        open={!!grantToRemove}
        onClose={closeRemoveModal}
        type="error"
        title="Delete grant"
        description="Are you sure? This cannot be undone."
        footer={(
          <>
            <Button variant="secondary" onClick={closeRemoveModal} disabled={removeSubmitting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmRemove} disabled={removeSubmitting}>
              {removeSubmitting ? 'Deleting…' : 'Delete grant'}
            </Button>
          </>
        )}
      >
        {removeError && <Alert color="error" description={removeError} />}
      </Modal>
    </CrmPageLayout>
  );
}

GrantsPage.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  csrfToken: PropTypes.string,
  owners: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), label: PropTypes.string })),
  currentOwnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stages: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
};

export default GrantsPage;
